<script>
import axios from 'axios';
import QueryBar from './QueryBar.vue';
import FacetsSidebar from './FacetsSidebar.vue';
import TimelineChart from './TimelineChart.vue';
import ResultsTable from './ResultsTable.vue';
import IssuesPanel from './IssuesPanel.vue';
import SlowSpansPanel from './SlowSpansPanel.vue';
import TraceDrawer from './TraceDrawer.vue';
import { TIME_RANGES } from './severity';
import { laravelParamsSerializer } from './paramsSerializer';

export default {
    name: 'LogsExplorer',
    components: {
        QueryBar,
        FacetsSidebar,
        TimelineChart,
        ResultsTable,
        IssuesPanel,
        SlowSpansPanel,
        TraceDrawer,
    },
    props: {
        entriesUrl: { type: String, required: true },
        overviewUrl: { type: String, required: true },
        issuesUrl: { type: String, required: true },
        issueUrl: { type: String, required: true },
        traceUrl: { type: String, required: true },
        spansUrl: { type: String, required: true },
    },
    data() {
        return {
            tab: 'explorer',
            filters: {
                search: '',
                levels: [],
                channels: [],
                rangeKey: '24h',
            },
            page: 1,
            perPage: 50,
            loading: false,
            error: null,
            overviewError: null,
            entriesPage: { entries: [], total: 0, page: 1, per_page: 50 },
            facets: { levels: {}, channels: {} },
            timeline: [],
            traceId: null,
            paramsSerializer: { serialize: laravelParamsSerializer },
        };
    },
    computed: {
        range() {
            return TIME_RANGES.find((r) => r.key === this.filters.rangeKey) ?? TIME_RANGES[2];
        },
    },
    created() {
        this.refresh();
    },
    methods: {
        queryParams() {
            const to = new Date();
            const from = new Date(to.getTime() - this.range.minutes * 60000);

            // Entries and overview get the very same filters, so the chart
            // can only ever show the rows the table is showing.
            return {
                search: this.filters.search || undefined,
                levels: this.filters.levels.length ? this.filters.levels : undefined,
                channels: this.filters.channels.length ? this.filters.channels : undefined,
                from: from.toISOString(),
                to: to.toISOString(),
            };
        },
        formatAxiosError(e, fallback) {
            const data = e.response?.data;
            if (!data) {
                return fallback;
            }

            const message = data.message ?? fallback;
            const errors = data.errors;
            if (!errors || typeof errors !== 'object') {
                return message;
            }

            const detail = Object.values(errors)
                .flat()
                .filter(Boolean)
                .join(' ');

            return detail ? `${message} ${detail}` : message;
        },
        async refresh() {
            this.loading = true;
            this.error = null;
            this.overviewError = null;

            const params = this.queryParams();
            const axiosOpts = { paramsSerializer: this.paramsSerializer };

            const [entriesResult, overviewResult] = await Promise.allSettled([
                axios.get(this.entriesUrl, {
                    ...axiosOpts,
                    params: { ...params, page: this.page, per_page: this.perPage },
                }),
                axios.get(this.overviewUrl, {
                    ...axiosOpts,
                    params: { ...params, bucket_minutes: this.range.bucketMinutes },
                }),
            ]);

            if (entriesResult.status === 'fulfilled') {
                this.entriesPage = entriesResult.value.data;
            } else {
                this.error = this.formatAxiosError(
                    entriesResult.reason,
                    'Failed to load log entries.',
                );
            }

            if (overviewResult.status === 'fulfilled') {
                this.facets = overviewResult.value.data.facets;
                this.timeline = overviewResult.value.data.timeline;
            } else {
                this.overviewError = this.formatAxiosError(
                    overviewResult.reason,
                    'Failed to load facets / timeline.',
                );
            }

            this.loading = false;
        },
        runQuery() {
            this.page = 1;
            this.refresh();
        },
        toggleLevel(level) {
            this.filters.levels = this.toggleIn(this.filters.levels, level);
            this.runQuery();
        },
        toggleChannel(channel) {
            this.filters.channels = this.toggleIn(this.filters.channels, channel);
            this.runQuery();
        },
        clearDimension(dimension) {
            this.filters[dimension] = [];
            this.runQuery();
        },
        toggleIn(selection, value) {
            return selection.includes(value)
                ? selection.filter((item) => item !== value)
                : [...selection, value];
        },
        changePage(page) {
            this.page = page;
            this.refresh();
        },
        openTrace(correlationId) {
            this.traceId = correlationId;
        },
        openIssue(fingerprint) {
            this.tab = 'issues';
            this.$nextTick(() => this.$refs.issuesPanel?.openFingerprint(fingerprint));
        },
    },
};
</script>

<template>
    <div class="logs-explorer">
        <div class="box box-primary">
            <div class="box-header with-border">
                <h1 class="box-title" style="margin-right: 20px">
                    <i class="fa fa-search"></i> Logs Explorer
                </h1>
                <ul class="nav nav-pills" style="display: inline-flex; vertical-align: middle">
                    <li :class="{ active: tab === 'explorer' }">
                        <a href="#" @click.prevent="tab = 'explorer'">Explorer</a>
                    </li>
                    <li :class="{ active: tab === 'issues' }">
                        <a href="#" @click.prevent="tab = 'issues'">Issues</a>
                    </li>
                    <li :class="{ active: tab === 'spans' }">
                        <a href="#" @click.prevent="tab = 'spans'">Slow Spans</a>
                    </li>
                </ul>
            </div>

            <div class="box-body">
                <div v-show="tab === 'explorer'">
                    <query-bar
                        :filters="filters"
                        :loading="loading"
                        @run="runQuery"
                        @reload="refresh"
                    />

                    <div v-if="error" class="alert alert-danger" style="margin-top: 10px">{{ error }}</div>
                    <div v-if="overviewError" class="alert alert-warning" style="margin-top: 10px">{{ overviewError }}</div>

                    <div class="row" style="margin-top: 10px">
                        <div class="col-md-2">
                            <facets-sidebar
                                :facets="facets"
                                :active-levels="filters.levels"
                                :active-channels="filters.channels"
                                @toggle-level="toggleLevel"
                                @toggle-channel="toggleChannel"
                                @clear="clearDimension"
                            />
                        </div>
                        <div class="col-md-10">
                            <timeline-chart :timeline="timeline" />

                            <results-table
                                :page="entriesPage"
                                :loading="loading"
                                @change-page="changePage"
                                @open-trace="openTrace"
                                @open-issue="openIssue"
                            />
                        </div>
                    </div>
                </div>

                <div v-show="tab === 'issues'">
                    <issues-panel
                        ref="issuesPanel"
                        :issues-url="issuesUrl"
                        :issue-url="issueUrl"
                        @open-trace="openTrace"
                    />
                </div>

                <div v-show="tab === 'spans'">
                    <slow-spans-panel
                        :spans-url="spansUrl"
                        @open-trace="openTrace"
                    />
                </div>
            </div>
        </div>

        <trace-drawer
            v-if="traceId"
            :trace-url="traceUrl"
            :correlation-id="traceId"
            @close="traceId = null"
        />
    </div>
</template>
